import * as React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ActionConfig {
  label: string;
  onClick: () => void;
  icon?: React.ComponentType<{ className?: string }>;
}

interface SecondaryActionConfig {
  label: string;
  onClick: () => void;
}

interface EmptyStateProps {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  body?: React.ReactNode;
  /** Primary call-to-action. Renders as a shadcn Button. */
  action?: ActionConfig;
  /** Optional secondary CTA for read-the-docs-style links. */
  secondaryAction?: SecondaryActionConfig;
  /** 'card' (default) for centred page emptiness, 'banner' for soft first-run tint. */
  variant?: 'card' | 'banner';
  className?: string;
}

/**
 * Shared empty state. Replaces a mix of bare-text "no items yet" blocks
 * (WorkItemsPage, ProjectsPage, TranscriptsPage) and the richer first-run
 * banner on FocusTimePage.
 *
 * Two visual variants:
 *   - card   : centred Card with muted icon and stacked title/body/CTA.
 *   - banner : tinted primary card with a left-aligned icon row, matching
 *              FocusTimePage's first-run explainer.
 */
export function EmptyState({
  icon: Icon,
  title,
  body,
  action,
  secondaryAction,
  variant = 'card',
  className,
}: EmptyStateProps) {
  if (variant === 'banner') {
    return (
      <Card className={cn('border-primary/25 bg-primary/[0.04]', className)}>
        <CardContent className="flex items-start gap-3 pt-6">
          {Icon && <Icon className="mt-0.5 h-5 w-5 text-primary" />}
          <div className="flex-1">
            <p className="mb-1 text-sm font-semibold">{title}</p>
            {body && <p className="mb-2 text-sm text-muted-foreground">{body}</p>}
            <EmptyStateActions action={action} secondaryAction={secondaryAction} />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
        {Icon && <Icon className="h-8 w-8 text-muted-foreground" />}
        <p className="text-base font-semibold">{title}</p>
        {body && <div className="max-w-md text-sm text-muted-foreground">{body}</div>}
        {(action || secondaryAction) && (
          <div className="mt-2 flex items-center gap-2">
            <EmptyStateActions action={action} secondaryAction={secondaryAction} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EmptyStateActions({
  action,
  secondaryAction,
}: {
  action?: ActionConfig;
  secondaryAction?: SecondaryActionConfig;
}) {
  if (!action && !secondaryAction) return null;
  const ActionIcon = action?.icon;
  return (
    <div className="flex items-center gap-2">
      {action && (
        <Button size="sm" onClick={action.onClick} className="gap-1.5">
          {ActionIcon && <ActionIcon className="h-4 w-4" />}
          {action.label}
        </Button>
      )}
      {secondaryAction && (
        <Button size="sm" variant="ghost" onClick={secondaryAction.onClick}>
          {secondaryAction.label}
        </Button>
      )}
    </div>
  );
}
